import { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import {
  Camera, Utensils, Flame, Beef, Wheat, Droplet, Check, Plus, Minus, Trash2,
  X, Info, Sparkles, TrendingUp, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { downscaleImage } from "@/lib/image";
import { Loader } from "@/components/common";

const MACROS = [
  { key: "calories", label: "Calories", unit: "kcal", icon: Flame },
  { key: "protein_g", label: "Protein", unit: "g", icon: Beef },
  { key: "carbs_g", label: "Carbs", unit: "g", icon: Wheat },
  { key: "fat_g", label: "Fat", unit: "g", icon: Droplet },
];

function scaled(it) {
  const bq = it.baseQty || 1;
  const f = (it.quantity || 1) / bq;
  return {
    calories: Math.round((it.base.calories || 0) * f),
    protein_g: Math.round((it.base.protein_g || 0) * f),
    carbs_g: Math.round((it.base.carbs_g || 0) * f),
    fat_g: Math.round((it.base.fat_g || 0) * f),
  };
}

export default function Nutrition() {
  const { mode, isSenior } = useAppMode();
  const [summary, setSummary] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [note, setNote] = useState("");
  const [items, setItems] = useState(null); // review list or null
  const [saving, setSaving] = useState(false);
  const [meals, setMeals] = useState([]);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/nutrition/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/nutrition/meals").then((r) => setMeals(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/image\//.test(f.type)) { toast.error("Please choose a photo of your food."); return; }
    setScanning(true); setItems(null); setNote("");
    try {
      const b64 = await downscaleImage(f);
      const { data } = await api.post("/nutrition/scan", { image_base64: b64, mode });
      if (data.error || !(data.items || []).length) {
        toast.info(data.note || "No food detected — try a clearer photo, or add items manually.");
        setItems([]);
      } else {
        setItems((data.items || []).map((it) => ({
          name: it.name || "Food", portion: it.portion || "", quantity: Number(it.quantity) || 1,
          baseQty: Number(it.quantity) || 1,
          base: { calories: it.calories || 0, protein_g: it.protein_g || 0, carbs_g: it.carbs_g || 0, fat_g: it.fat_g || 0 },
        })));
      }
      setNote(data.note || "These are rough estimates — a photo can't know exact ingredients or oil used.");
    } catch { toast.error("Could not scan that photo."); }
    finally { setScanning(false); }
  };

  const totals = items ? items.reduce((t, it) => {
    const s = scaled(it);
    return { calories: t.calories + s.calories, protein_g: t.protein_g + s.protein_g, carbs_g: t.carbs_g + s.carbs_g, fat_g: t.fat_g + s.fat_g };
  }, { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }) : null;

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const addItem = () => setItems((arr) => [...(arr || []), { name: "", portion: "", quantity: 1, baseQty: 1, base: { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 } }]);

  const confirm = async () => {
    const clean = items.filter((it) => it.name.trim());
    if (!clean.length) { toast.info("Add at least one food item."); return; }
    setSaving(true);
    try {
      const payloadItems = clean.map((it) => ({ name: it.name, portion: it.portion, quantity: it.quantity, ...scaled(it) }));
      await api.post("/nutrition/meals", { items: payloadItems, totals, title: payloadItems.map((i) => i.name).slice(0, 3).join(", ") });
      toast.success("Meal added to your nutrition history 🍽️");
      setItems(null); setNote(""); load();
    } catch { toast.error("Could not save meal."); }
    finally { setSaving(false); }
  };

  const deleteMeal = async (id) => { await api.delete(`/nutrition/meals/${id}`); setMeals((m) => m.filter((x) => x.id !== id)); load(); };

  const big = isSenior;
  const maxCal = Math.max(1, ...(summary?.trend || []).map((d) => d.calories));

  return (
    <div className="space-y-8">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className={`font-head font-extrabold flex items-center gap-3 ${big ? "text-4xl" : "text-3xl sm:text-4xl"}`}><Utensils className="w-8 h-8 text-primary" />Nutrition</h1>
          <p className={`text-muted-foreground mt-2 ${big ? "text-xl" : ""}`}>Snap your meals for gentle, general awareness of what you eat. No dieting, no judgement — just estimates.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} data-testid="scan-food-btn"
          className={`inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold hover:brightness-110 active:scale-95 transition ${big ? "px-7 py-4 text-xl" : "px-6 py-3"}`}>
          <Camera className={big ? "w-6 h-6" : "w-5 h-5"} /> Scan Food 📸
        </button>
      </div>

      {scanning && <Loader label="Looking at your meal…" />}

      {/* Review card */}
      {items && !scanning && (
        <div className="rounded-lg border-2 border-primary/40 bg-card p-5 sm:p-6 space-y-4 animate-rise" data-testid="scan-review">
          <div className="flex items-center justify-between">
            <h2 className="font-head font-bold text-xl">Does this look correct?</h2>
            <button onClick={() => setItems(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex items-start gap-2 rounded-md bg-secondary/60 p-3 text-sm text-muted-foreground">
            <Info className="w-4 h-4 shrink-0 mt-0.5" /> {note || "Estimates only. Edit anything below to correct it."}
          </div>

          <div className="space-y-3">
            {items.map((it, i) => {
              const s = scaled(it);
              return (
                <div key={i} className="rounded-lg border border-border bg-background p-4" data-testid={`scan-item-${i}`}>
                  <div className="flex items-center gap-2">
                    <input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} data-testid={`item-name-${i}`}
                      placeholder="Food name" className="flex-1 rounded-md border border-input bg-card px-3 py-2 font-semibold outline-none focus:ring-2 focus:ring-ring" />
                    <button onClick={() => removeItem(i)} data-testid={`item-remove-${i}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <input value={it.portion} onChange={(e) => setItem(i, { portion: e.target.value })} data-testid={`item-portion-${i}`}
                      placeholder="Portion (e.g. 1 cup)" className="w-40 rounded-md border border-input bg-card px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Qty</span>
                      <button onClick={() => setItem(i, { quantity: Math.max(0.5, +(it.quantity - 0.5).toFixed(1)) })} data-testid={`item-qty-minus-${i}`} className="h-7 w-7 grid place-items-center rounded-md border border-border"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-8 text-center font-bold tabular-nums" data-testid={`item-qty-${i}`}>{it.quantity}</span>
                      <button onClick={() => setItem(i, { quantity: +(it.quantity + 0.5).toFixed(1) })} data-testid={`item-qty-plus-${i}`} className="h-7 w-7 grid place-items-center rounded-md border border-border"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                    <span className="text-primary font-semibold">{s.calories} kcal</span>
                    <span>P {s.protein_g}g</span><span>C {s.carbs_g}g</span><span>F {s.fat_g}g</span>
                  </div>
                </div>
              );
            })}
            <button onClick={addItem} data-testid="add-item" className="w-full inline-flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-border py-2.5 text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary"><Plus className="w-4 h-4" />Add a food item</button>
          </div>

          {/* estimated totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {MACROS.map((m) => (
              <div key={m.key} className="rounded-md bg-primary/10 p-3 text-center" data-testid={`review-total-${m.key}`}>
                <m.icon className="w-4 h-4 text-primary mx-auto" />
                <div className="font-head font-extrabold text-xl mt-1">{totals[m.key]}</div>
                <div className="text-[10px] text-muted-foreground">{m.label} ({m.unit})</div>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"><Pencil className="w-4 h-4" />Rescan</button>
            <button onClick={confirm} disabled={saving} data-testid="confirm-meal" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-bold py-3 hover:brightness-110 disabled:opacity-50 transition"><Check className="w-5 h-5" />Confirm & save meal</button>
          </div>
        </div>
      )}

      {/* Today totals */}
      <section>
        <h2 className="font-head font-bold text-lg mb-3">Today</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="today-totals">
          {MACROS.map((m) => (
            <div key={m.key} className="rounded-lg border border-border bg-card p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><m.icon className="w-5 h-5" /></div>
              <div><div className="font-head font-extrabold text-2xl">{summary?.today_totals?.[m.key] ?? 0}</div><div className="text-xs text-muted-foreground">{m.label} ({m.unit})</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* 7-day trend */}
      <section className="rounded-lg border border-border bg-card p-5" data-testid="trend-card">
        <h2 className="font-head font-bold text-lg mb-1 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Last 7 days</h2>
        <p className="text-xs text-muted-foreground mb-3">Estimated daily calories — a simple awareness trend, not a target.</p>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary?.trend || []}>
              <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Bar dataKey="calories" radius={[6, 6, 0, 0]}>
                {(summary?.trend || []).map((d, i) => <Cell key={i} fill={d.calories >= maxCal ? "hsl(var(--primary))" : "hsl(var(--accent))"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Meal history */}
      <section>
        <h2 className="font-head font-bold text-lg mb-3">Meal history</h2>
        {!meals.length ? (
          <p className="text-muted-foreground text-sm">No meals yet. Tap “Scan Food 📸” to add your first meal.</p>
        ) : (
          <div className="space-y-2" data-testid="meal-history">
            {meals.map((m) => (
              <div key={m.id} className="rounded-lg border border-border bg-card p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/15 text-accent grid place-items-center shrink-0"><Utensils className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.title}</div>
                  <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {m.totals?.calories} kcal · P{m.totals?.protein_g} C{m.totals?.carbs_g} F{m.totals?.fat_g}</div>
                </div>
                <button onClick={() => deleteMeal(m.id)} data-testid={`meal-del-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
